<template>
    <span class="gh-tour-controller" aria-hidden="true"></span>
</template>

<script setup lang="ts">
    import { onBeforeUnmount, watch } from 'vue';
    import {
        tourTooltipService,
        type TourTooltipStartOptions,
        type TourTooltipStep,
        type TourTooltipStopOptions,
        type TourTooltipUpdateOptions,
    } from '@/utils/tour-tooltip.utils';

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            steps?: TourTooltipStep[];
            mask?: boolean;
            useCookie?: boolean;
        }>(),
        {
            steps: () => [],
            mask: false,
            useCookie: true,
        }
    );

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'close'): void;
    }>();

    // ----- state -----
    const controllerId = `tour-tooltip-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

    // ----- methods -----
    // 導覽流程結束時通知外層頁面
    const handleClose = (): void => {
        emits('close');
    };

    // 組合目前元件對應的 service 啟動設定
    const buildServiceOptions = (): TourTooltipStartOptions => {
        return {
            controlled: true,
            controllerId,
            steps: props.steps,
            mask: props.mask,
            useCookie: props.useCookie,
            onClose: handleClose,
        };
    };

    // 開啟目前元件控制的 tour 流程
    const openTour = (): void => {
        tourTooltipService.start(buildServiceOptions());
    };

    // 關閉目前元件控制的 tour 流程
    const closeTour = (options: TourTooltipStopOptions = {}): void => {
        const preserveRuntime = options.preserveRuntime !== false;
        const emitClose = options.emitClose !== false;

        tourTooltipService.stop({
            controllerId,
            preserveRuntime,
            emitClose,
        });
    };

    // 同步步驟、遮罩與 cookie 設定
    const syncTourOptions = (
        options: Omit<TourTooltipUpdateOptions, 'controllerId' | 'onClose'> = {}
    ): void => {
        tourTooltipService.update({
            controllerId,
            ...options,
            onClose: handleClose,
        });
    };

    // ----- watch -----
    watch(
        () => props.steps,
        (steps) => {
            syncTourOptions({ steps });
        },
        {
            deep: true,
        }
    );

    watch(
        () => props.mask,
        (mask) => {
            syncTourOptions({ mask });
        }
    );

    watch(
        () => props.useCookie,
        (useCookie) => {
            syncTourOptions({ useCookie });
        }
    );

    // ----- lifecycle -----
    onBeforeUnmount(() => {
        closeTour({
            preserveRuntime: false,
            emitClose: false,
        });
    });

    // ----- expose -----
    defineExpose({
        openTour,
        closeTour,
        syncTourOptions,
    });
</script>

<style lang="scss" scoped>
    .gh-tour-controller {
        display: none;
    }
</style>
